import { useMemo, useState } from 'react';
import useSWRInfinite from 'swr/infinite';
import dayjs from 'dayjs';
import { apiUrls } from '../utils/apiUtils';
import { fetcher } from '../utils/fetcher';
import useDebounce from './useDebounce';

interface Pagination {
  hasNext: boolean;
  nextCursor: string;
  pageSizes: number;
}

// Interface for API response
interface TransactionsApiResponse {
  data: Transaction[];
  pagination: Pagination;
}

const getCurrentMonthRange = (): { from: Date; to: Date } => {
  const now = dayjs();
  const start = now.startOf('month');
  const end = now.endOf('month');
  return { from: start.toDate(), to: end.toDate() };
};

/**
 * Hook to manage transaction history with pagination
 * @returns Transaction data and related utilities
 */
export const useHistory = () => {
  const currentMonth = getCurrentMonthRange();

  // Single state for all filters to allow atomic updates
  const [filters, setFilters] = useState<{
    walletId?: string;
    fromDate?: Date;
    toDate?: Date;
    categoryId?: string;
    query?: string;
  }>({
    walletId: undefined,
    fromDate: currentMonth.from,
    toDate: currentMonth.to,
    categoryId: undefined,
    query: undefined,
  });

  // Debounce query to prevent excessive API calls while typing
  const debouncedQuery = useDebounce(filters.query, 500);


  // Function to get key for SWR based on page index
  const getKey = (pageIndex: number, previousPageData: TransactionsApiResponse) => {
    // Reached the end
    if (previousPageData && !previousPageData.pagination.hasNext) return null;

    // Construct params for API call
    const queryParams: Record<string, string> = {};

    // Add cursor from previous page if available
    if (previousPageData?.pagination.hasNext) {
      queryParams.nextCursor = previousPageData.pagination.nextCursor;
    }

    // Add filters if set
    if (filters.walletId && filters.walletId !== 'all') {
      queryParams.walletId = filters.walletId;
    }

    if (filters.fromDate) {
      queryParams.fromDate = filters.fromDate.toISOString();
    }

    if (filters.toDate) {
      queryParams.toDate = filters.toDate.toISOString();
    }

    if (filters.categoryId && filters.categoryId !== 'all') {
      queryParams.categoryId = filters.categoryId;
    }

    if (debouncedQuery) {
      queryParams.query = debouncedQuery;
    }

    // Generate URL with apiUrls helper
    return apiUrls.transactions(queryParams);
  };

  // Fetch transactions data using SWR with optimized configuration
  const { data, error, mutate, size, setSize, isValidating } =
    useSWRInfinite<TransactionsApiResponse>(
      getKey,
      fetcher,
      {
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        revalidateIfStale: false,
        dedupingInterval: 5000,
        keepPreviousData: true,
      }
    );

  // isLoadingMore: true when fetching additional pages (not initial load)
  const isLoadingMore = isValidating && data && data.length < size;

  // Transform API response into transactions with deduplication
  const transactions = useMemo(() => {
    if (!data) return [];
    const allTransactions = data.flatMap((_data) => _data.data);
    // Deduplicate by id to prevent duplicates from pagination issues
    const seen = new Set<string>();
    return allTransactions.filter((tx) => {
      if (seen.has(tx.id)) return false;
      seen.add(tx.id);
      return true;
    });
  }, [data]);

  // Get pagination info from last page
  const pagination: Pagination = useMemo(() => {
    return (
      data?.[data.length - 1]?.pagination || {
        hasNext: false,
        nextCursor: '',
        pageSizes: 0,
      }
    );
  }, [data]);

  /**
   * Load the next page of results
   */
  const loadMore = () => {
    if (pagination.hasNext) {
      setSize(size + 1);
    }
  };

  /**
   * Filter transactions by wallet ID
   * @param walletId The wallet ID to filter by
   */
  const setWalletId = (walletId: string) => {
    updateFilters({ walletId });
  };

  /**
   * Filter transactions by date range
   * @param payload Object containing fromDate and toDate
   */
  const setRangeDate = (payload: { fromDate?: Date, toDate?: Date }) => {
    const newFromDate = payload?.fromDate;
    const newToDate = payload?.toDate;
    const fromChanged = newFromDate?.getTime() !== filters.fromDate?.getTime();
    const toChanged = newToDate?.getTime() !== filters.toDate?.getTime();
    if (fromChanged || toChanged) {
      updateFilters({ fromDate: newFromDate, toDate: newToDate });
    }
  };

  /**
   * Filter transactions by category ID
   * @param categoryId The category ID to filter by
   */
  const setCategoryId = (categoryId: string) => {
    updateFilters({ categoryId });
  };

  /**
   * Search transactions by description or amount
   * @param query Search string
   */
  const setQuery = (query: string) => {
    updateFilters({ query: query || undefined });
  };

  const rangeDate = useMemo(() => {
    return {
      fromDate: filters.fromDate,
      toDate: filters.toDate,
    };
  }, [filters.fromDate, filters.toDate]);

  // Single update helper to atomically change filters and reset pagination once
  const updateFilters = (partial: Partial<typeof filters>) => {
    setFilters((prev) => ({ ...prev, ...partial }));
    if (size !== 1) {
      setSize(1);
    }
  };

  return {
    transactions,
    isLoading: !data && !error,
    isLoadingMore,
    isError: error,
    hasNext: pagination.hasNext,
    loadMore,
    mutate,
    setWalletId,
    setRangeDate,
    setCategoryId,
    setQuery,
    rangeDate,
  };
};

export default useHistory;
